import {
  HydratedOpportunity,
  isBlankCustomFieldValue,
  type CustomFieldValue,
  type CustomFieldValues,
} from "@forgepoint/shared";
import { useUserDisplayNames } from "@/store/teamsStore";
import {
  Archive,
  User as UserIcon,
  Ruler,
  CircleDot,
  CalendarDays,
} from "lucide-react";
import { format } from "date-fns";
import Decimal from "decimal.js";
import { OpportunityWorkingIndicator } from "./OpportunityWorkingIndicator";
import { OpportunityQuoteBadges } from "./OpportunityQuoteBadges";
import { customFieldId } from "@/lib/customFieldFilters";
import {
  companyCustomFieldId,
  OpportunityCardField,
  quoteCustomFieldId,
} from "./opportunityCardFields";

/** Kanban items have status widened to string (overridden with stageId for column grouping). */
export type KanbanOpportunity = Omit<HydratedOpportunity, "status"> & {
  status: string;
};

interface OpportunityKanbanCardProps {
  opportunity: KanbanOpportunity;
  visibleFieldIds: string[];
  customFieldNames: string[];
  quoteCustomFieldNames: string[];
  companyCustomFieldNames: string[];
}

/**
 * Renders a compact card for an opportunity in the kanban board.
 * The name and the inline archived and working signals always show.
 * Every other row shows when the viewer selected its field and the opportunity
 * has a value. A quote custom field row lists the distinct values across the
 * opportunity's quotes. A company custom field row shows the value on the
 * opportunity's company.
 * @param opportunity - The hydrated opportunity to display
 * @param visibleFieldIds - Ids of the fields the viewer chose to show
 * @param customFieldNames - The organization's opportunity custom field names, in schema order
 * @param quoteCustomFieldNames - The organization's quote custom field names, in schema order
 * @param companyCustomFieldNames - The organization's company custom field names, in schema order
 */
export function OpportunityKanbanCard({
  opportunity,
  visibleFieldIds,
  customFieldNames,
  quoteCustomFieldNames,
  companyCustomFieldNames,
}: OpportunityKanbanCardProps) {
  const displayNames = useUserDisplayNames();
  const isWorking = opportunity.isPrimaryThreadWorking === true;
  const isArchived = opportunity.archivedAt !== null;
  const isVisible = (field: OpportunityCardField) =>
    visibleFieldIds.includes(field);

  const salesRepName = opportunity.salesRepUserId
    ? (displayNames.get(opportunity.salesRepUserId) ?? null)
    : null;

  const estimatorLabel = (() => {
    const names = opportunity.estimatorUserIds
      .map((id) => displayNames.get(id))
      .filter((name): name is string => name !== undefined);
    if (names.length === 0) {
      return null;
    }
    return names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0];
  })();

  const formattedAmount = new Decimal(opportunity.amount)
    .toNumber()
    .toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const customFieldRows = [
    ...buildCustomFieldRows(customFieldNames, visibleFieldIds, customFieldId, [
      opportunity.customFields ?? {},
    ]),
    ...buildCustomFieldRows(
      quoteCustomFieldNames,
      visibleFieldIds,
      quoteCustomFieldId,
      opportunity.quoteCustomFields ?? [],
    ),
    ...buildCustomFieldRows(
      companyCustomFieldNames,
      visibleFieldIds,
      companyCustomFieldId,
      [opportunity.company?.customFields ?? {}],
    ),
  ];

  const pendingActionOwnerNames = opportunity.pendingActionOwnerNames ?? [];

  return (
    <div className={isArchived ? "opacity-50" : ""}>
      <div className="flex items-start gap-1">
        <h4 className="font-semibold text-sm min-w-0 flex-1 line-clamp-2">
          {opportunity.name}
        </h4>
        <div className="flex h-5 shrink-0 items-center gap-1">
          {isArchived && (
            <span role="img" aria-label="Archived" title="Archived">
              <Archive className="h-3 w-3 text-muted-foreground" aria-hidden />
            </span>
          )}
          {isWorking && <OpportunityWorkingIndicator />}
        </div>
      </div>

      {isVisible(OpportunityCardField.QUOTE_NUMBERS) && (
        <OpportunityQuoteBadges quoteNumbers={opportunity.quoteNumbers ?? []} />
      )}

      {isVisible(OpportunityCardField.COMPANY) && opportunity.company && (
        <p className="text-xs text-muted-foreground truncate mt-0.5">
          {opportunity.company.name}
        </p>
      )}

      {isVisible(OpportunityCardField.SALES_REP) && salesRepName && (
        <div className="flex items-center gap-1 mt-0.5">
          <UserIcon className="h-3 w-3 text-muted-foreground shrink-0" />
          <p className="text-xs text-muted-foreground truncate">
            {salesRepName}
          </p>
        </div>
      )}

      {isVisible(OpportunityCardField.ESTIMATORS) && estimatorLabel && (
        <div className="flex items-center gap-1 mt-0.5">
          <Ruler className="h-3 w-3 text-muted-foreground shrink-0" />
          <p className="text-xs text-muted-foreground truncate">
            {estimatorLabel}
          </p>
        </div>
      )}

      {isVisible(OpportunityCardField.CREATED_AT) && (
        <div className="flex items-center gap-1 mt-0.5">
          <CalendarDays className="h-3 w-3 text-muted-foreground shrink-0" />
          <p className="text-xs text-muted-foreground truncate">
            {format(opportunity.createdAt, "M/d/yyyy")}
          </p>
        </div>
      )}

      {customFieldRows.map((row) => (
        <p
          key={row.id}
          className="text-xs text-muted-foreground truncate mt-0.5"
        >
          {row.name}: {row.text}
        </p>
      ))}

      {(isVisible(OpportunityCardField.AMOUNT) ||
        isVisible(OpportunityCardField.PROBABILITY)) && (
        <div className="flex items-baseline justify-between mt-1">
          {isVisible(OpportunityCardField.AMOUNT) && (
            <p className="text-sm font-semibold">${formattedAmount}</p>
          )}
          {isVisible(OpportunityCardField.PROBABILITY) && (
            <span className="text-xs font-medium tabular-nums text-muted-foreground ml-auto">
              {opportunity.probability}%
            </span>
          )}
        </div>
      )}

      {isVisible(OpportunityCardField.PENDING_ACTION) &&
        pendingActionOwnerNames.length > 0 && (
          <div className="flex items-center gap-1 mt-1.5">
            <CircleDot className="h-3 w-3 text-warning shrink-0" />
            <span className="text-[10px] text-warning-foreground truncate">
              Awaiting action from {pendingActionOwnerNames.join(", ")}
            </span>
          </div>
        )}
    </div>
  );
}

/**
 * Builds a card row for each visible custom field with a value. The row text joins
 * the distinct formatted values across the value sets.
 */
function buildCustomFieldRows(
  fieldNames: string[],
  visibleFieldIds: string[],
  idFor: (fieldName: string) => string,
  valueSets: CustomFieldValues[],
): { id: string; name: string; text: string }[] {
  return fieldNames.flatMap((fieldName) => {
    const id = idFor(fieldName);
    if (!visibleFieldIds.includes(id)) {
      return [];
    }
    const texts = valueSets
      .map((values) => values[fieldName])
      .filter((value) => !isBlankCustomFieldValue(value))
      .map(formatCustomFieldValue);
    const distinctTexts = [...new Set(texts)];
    if (distinctTexts.length === 0) {
      return [];
    }
    return [{ id, name: fieldName, text: distinctTexts.join(", ") }];
  });
}

/**
 * Formats a non-blank custom field value for a card row.
 * @param value - The opportunity's value for the field
 * @returns The display text
 */
function formatCustomFieldValue(value: CustomFieldValue | undefined): string {
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  return String(value);
}
